import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import { usePageMeta } from '@/hooks/usePageMeta';

const values = [
  { icon: 'ri-focus-3-line', title: '内容优先', desc: '以影迷需求为中心，精选值得一看的高清电影内容。' },
  { icon: 'ri-layout-grid-line', title: '便捷浏览', desc: '清晰的分类与筛选，让你用更少时间找到想看的电影。' },
  { icon: 'ri-shield-check-line', title: '纯粹体验', desc: '没有繁杂的弹窗与广告，专注阅读与发现本身。' },
];

const team = [
  { name: '内容策划组', role: '负责影片资料整理与分类运营', icon: 'ri-book-2-line' },
  { name: '产品设计组', role: '负责网站体验与视觉呈现', icon: 'ri-palette-line' },
  { name: '技术团队', role: '负责站点搭建与稳定运行', icon: 'ri-code-s-slash-line' },
];

export default function About() {
  usePageMeta({
    title: '关于我们 - 高清电影',
    description: '了解高清电影网站的定位、团队与版权信息。我们专注高清电影内容分享与推荐，帮你快速发现值得一看的电影。',
    keywords: '关于高清电影,高清电影网站,电影推荐平台',
  });

  return (
    <PageLayout>
      <div className="w-full">
        {/* Hero */}
        <section className="bg-primary-900">
          <div className="w-full px-4 py-14 md:px-8 md:py-20">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-white">
                <i className="ri-information-line"></i>
                关于我们
              </span>
              <h1 className="mt-4 font-heading text-3xl font-black text-white md:text-5xl">
                关于「高清电影」
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-background-300 md:text-base">
                高清电影是一个面向影迷的电影内容分享与推荐平台，致力于让「发现好电影」这件事变得简单而愉悦。
              </p>
            </div>
          </div>
        </section>

        <div className="w-full px-4 py-14 md:px-8 md:py-16">
          {/* Positioning */}
          <section className="mx-auto max-w-4xl">
            <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-flag-2-line text-lg"></i>
              </span>
              网站定位
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700">
              <p>
                我们专注高清电影的分享与推荐，涵盖动作、喜剧、剧情、科幻、爱情、悬疑、动画、冒险等多种类型。
                无论你是想追最新的热门影片，还是想按类型慢慢挑选，这里都能帮你快速找到方向。
              </p>
              <p>
                站内的影片资料、评分、热度等信息均为演示数据，旨在展示电影门户网站的分类浏览、详情查询与相关推荐等使用体验。
                本站<strong className="text-foreground-900">不提供真实的在线播放与下载服务</strong>，请以正版渠道欣赏影片。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                    <i className={`${v.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Team */}
          <section className="mx-auto mt-16 max-w-4xl">
            <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-500 text-white">
                <i className="ri-team-line text-lg"></i>
              </span>
              团队与版权
            </h2>
            <div className="mt-5 space-y-3">
              {team.map((t) => (
                <div
                  key={t.name}
                  className="flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-background-100 text-primary-500">
                    <i className={`${t.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950">{t.name}</h3>
                    <p className="text-sm text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-5 rounded-lg bg-background-100 px-4 py-4 text-sm leading-relaxed text-foreground-600">
              © 2026 高清电影 · 保留所有权利。本站为演示性质的电影门户网站，所有影片名称、人物、评分与数据均为虚构，
              不构成任何真实产品与服务。如需引用或反馈，欢迎通过下方邮箱与我们联系。
            </p>
          </section>

          {/* Contact */}
          <section className="mx-auto mt-16 max-w-4xl">
            <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-500 text-white">
                <i className="ri-mail-send-line text-lg"></i>
              </span>
              联系方式
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
                <p className="text-xs text-foreground-400">反馈邮箱</p>
                <p className="mt-1.5 flex items-center gap-2 text-sm font-bold text-foreground-900">
                  <i className="ri-mail-line text-primary-500"></i>
                  feedback@hdmovies-demo.com
                </p>
              </div>
              <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
                <p className="text-xs text-foreground-400">合作咨询</p>
                <p className="mt-1.5 flex items-center gap-2 text-sm font-bold text-foreground-900">
                  <i className="ri-customer-service-2-line text-primary-500"></i>
                  business@hdmovies-demo.com
                </p>
              </div>
            </div>
            <p className="mt-4 text-xs text-foreground-400">
              以上邮箱为演示文本，仅用于展示联系方式的呈现方式。
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-home-line text-lg"></i>
                返回首页
              </Link>
              <Link
                to="/search"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-bold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-search-line text-lg"></i>
                搜索电影
              </Link>
            </div>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}